import {FC, FormEvent, useEffect, useState} from "react";
import {useWarehouseLocks, WarehouseLockNotice} from "../../warehouse/useWarehouseLocks";
import {
    Alert,
    Button,
    CloseButton,
    Col,
    Form,
    FormControl,
    ListGroup,
    Modal,
    Row, Spinner
} from "react-bootstrap";
import {useAppDispatch, useAppSelector} from "../../hooks";
import {
    selectCalendarData,
    selectCurrentEmployee,
    selectReportCalendarEvent, selectSelectedEmployees, setReportCalendarEvent
} from "../../../features/AppStateSlice";
import {CancelIconButton} from "../../components/IconButtons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck} from "@fortawesome/free-solid-svg-icons";
import ReactDatePicker from "react-datepicker";
import {
    checkOverlapTimes,
    dateTimeToString,
    dateWithZeroTime,
    removeEmptyFields,
    showErrorsListInToast,
    showMsgInToast,
    stringToDate, timeDiffInMinutes, timeDiffToString, zeroToEmptyString
} from "../../tools";
import {collection, getDocs, query, updateDoc, where} from "firebase/firestore";
import {appFirestore} from "../../../index";
import EventFaceIcon from "./EventFaceIcon";
import NumberInputField from "../../components/inputs/NumberInputField";
import {calculateWorkhours} from "../../../firebase/FirebaseEmployeeStatisticsLoader";
import {RRCalendarDayType} from "../../types/RRCalendarDayType";
import {updateCalendarItemsWithChangeLog} from "./change-log/calendarChangeLog";

interface Props {
}

const ReportCalendarEvent: FC<Props> = (props) => {
    const dispatch = useAppDispatch();
    const calendarData = useAppSelector(selectCalendarData);
    const selectedEmployees = useAppSelector(selectSelectedEmployees);
    const editCalendarEvent = useAppSelector(selectReportCalendarEvent);
    const currentEmployee = useAppSelector(selectCurrentEmployee);
    const [endTime, setEndTime] = useState<Date>(editCalendarEvent?.event?.firstDate!);
    const [notes, setNotes] = useState<string>("");
    const [face, setFace] = useState<string>(editCalendarEvent?.event?.face || "NOT SET");
    const [restTime, setRestTime] = useState(editCalendarEvent?.item?.restTime || 0);
    const [bonus, setBonus] = useState(editCalendarEvent?.item?.bonus || 0);
    const [working, setWorking] = useState<boolean>(false);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const warehouseStatus = useWarehouseLocks();
    const lockAccess = warehouseStatus(editCalendarEvent?.item?.eventDayStart || editCalendarEvent?.calendarDay.date,
        editCalendarEvent?.item?.eventId || editCalendarEvent?.event?.id || "", editCalendarEvent?.item?.department);
    const isReadOnly = (editCalendarEvent?.isReadOnly ?? false) || lockAccess.blocked;

    useEffect(() => {
        setErrors([]);
        setEndTime(editCalendarEvent?.item?.eventDayEnd ||
            editCalendarEvent?.item?.eventDayStart ||
            stringToDate(editCalendarEvent?.calendarDay.date) ||
            new Date());
        setNotes(editCalendarEvent?.item?.notes || "");
        setRestTime(editCalendarEvent?.item?.restTime || 0);
        setBonus(editCalendarEvent?.item?.bonus || 0);
        setIsConfirmed(editCalendarEvent?.item?.isConfirmed || false);
        setFace(editCalendarEvent?.item?.face || "NOT SET");
    }, [editCalendarEvent])

    if (!editCalendarEvent) return null;

    const isSupervisor = (() => {
        if (!currentEmployee || currentEmployee.isAdmin) return true;
        const employeeItem = editCalendarEvent.calendarDay.items.find(
            value => value.employeeId === currentEmployee?.id && value.eventId === editCalendarEvent.event?.id
        );

        return (employeeItem && employeeItem.isSupervisor);
    })();

    function hideModal() {
        dispatch(setReportCalendarEvent(null))
    }

    function validateData(data: {eventDayEnd?: Date | null}) {
        if(isReadOnly)  {
            return false;
        }
        if(data.eventDayEnd && editCalendarEvent?.item?.eventDayStart && data.eventDayEnd < editCalendarEvent?.item?.eventDayStart) {
            setErrors(["Внимание! Крайният час е преди началния!"]);
            return false;
        }

        let eventFoundName: string = "";
        let calendarItemFound: RRCalendarDayType | undefined = undefined;
        if(editCalendarEvent?.item?.eventId !== "WAREHOUSE") {
            calendarData.forEach(
                value => {
                    if (calendarItemFound) {
                        return;
                    }

                    calendarItemFound = value.items.find(
                        item => {
                            return item.employeeId === editCalendarEvent?.item?.employeeId &&
                                item.id !== editCalendarEvent?.item?.id &&
                                item.eventId !== "WAREHOUSE" &&
                                item.eventDayEnd &&
                                data.eventDayEnd &&
                                checkOverlapTimes(item.eventDayStart, item.eventDayEnd, editCalendarEvent.item.eventDayStart, data.eventDayEnd)
                        }
                    );
                    if (calendarItemFound) {
                        eventFoundName = value.events.find(e => e.id === calendarItemFound?.eventId)?.eventName || "";
                    }
                }
            );
        }

        if (calendarItemFound) {
            console.error(calendarItemFound);
            setErrors([`Внимание! Служителят е зает на друго събитие: ${eventFoundName}!`]);
            return false;
        }
        return true;
    }

    async function handleFormSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (isReadOnly || working) return;
        try {
            setWorking(true);
            const data = {
                eventDayEnd: endTime,
                restTime: restTime || 0,
                bonus: bonus || 0,
                isConfirmed: isSupervisor ? isConfirmed || false : false,
                notes: isSupervisor ? notes || "" : undefined,
                face: isSupervisor ? (face || "NOT SET") : undefined,
            }

            if (editCalendarEvent?.item?.ref) {
                const _data = removeEmptyFields(data);

                if (!_data || !validateData(_data)) {
                    return;
                }


                if (_data) {
                    if (selectedEmployees.length) {
                        // debugger
                        const eventStart = dateWithZeroTime(editCalendarEvent?.item?.eventDayStart)!;
                        const eventEnd = new Date(eventStart);
                        eventEnd.setDate(eventEnd.getDate() + 1);
                        const calndarEvents = await getDocs(query(collection(appFirestore, "calendar"),
                            where("eventId", "==", editCalendarEvent?.item?.eventId),
                            where("eventDayStart", ">=", eventStart),
                            where("eventDayStart", "<", eventEnd)));

                        for (const employeeId of selectedEmployees) {
                                const emplEvent = calndarEvents.docs.find(d => d.get("employeeId") === employeeId &&
                                    (editCalendarEvent.item?.eventId !== "WAREHOUSE" ||
                                        (d.get("department") || "montage") === editCalendarEvent.item.department));
                                if (!emplEvent) throw new Error("Не е намерен запис за избран служител в този отдел.");
                                if (emplEvent) {
                                    const newData = {
                                        ...emplEvent.data(),
                                        employeeId: employeeId,
                                        eventDayEnd: _data.eventDayEnd,
                                        restTime: _data.restTime || 0,
                                        bonus: _data.bonus || 0,
                                        isConfirmed: _data.isConfirmed || false,
                                        notes: _data.notes || "",
                                        face: _data.face || "",
                                    };
                                    await updateCalendarItemsWithChangeLog([{
                                        ref: emplEvent.ref,
                                        data: newData,
                                        context: {
                                            eventNumber: editCalendarEvent.event?.eventNumber || editCalendarEvent.item.eventId,
                                            eventName: editCalendarEvent.event?.eventName || "",
                                        },
                                    }], currentEmployee);
                                }
                        }
                        if (endTime) {
                            // await calculateWorkhours(endTime, [...selectedEmployees])
                        }
                    } else {
                        await updateCalendarItemsWithChangeLog([{
                            ref: editCalendarEvent.item.ref,
                            data: _data,
                            context: {
                                eventNumber: editCalendarEvent.event?.eventNumber || editCalendarEvent.item.eventId,
                                eventName: editCalendarEvent.event?.eventName || "",
                            },
                        }], currentEmployee);
                        if (endTime) {
                            // await calculateWorkhours(endTime, [editCalendarEvent.item.employeeId])
                        }
                    }


                    if (editCalendarEvent?.event?.id !== "WAREHOUSE") {
                        let isDone = true;
                        const items = await getDocs(query(collection(appFirestore, "calendar"),
                            where("eventId", "==", editCalendarEvent.event!.id),
                            where("dayType", "==", "WORKDAY")
                        ));

                        const isNew = items.empty;
                        items.forEach(
                            i => {
                                if (!i.get("eventDayEnd") || i.get("isConfirmed") !== true) {
                                    isDone = false;
                                }
                            }
                        )
                        if (isNew) {
                            if (editCalendarEvent?.event?.eventStatus !== "НОВО") {
                                await updateDoc(editCalendarEvent!.event!.ref!, {eventStatus: "ПОТВЪРДЕНО"});
                            }
                        } else {
                            if (isDone && editCalendarEvent?.event?.eventStatus !== "ЗАВЪРШЕНО") {
                                await updateDoc(editCalendarEvent!.event!.ref!, {eventStatus: "ЗАВЪРШЕНО"});
                            }
                        }
                    }
                }
                dispatch(setReportCalendarEvent(null));
                showMsgInToast("Данните са запазени успешно!");
            }
        } catch
            (e) {
            showErrorsListInToast("Възникна проблем при запазването на данните:",
                [e instanceof Error ? e.message : "Неуспешен запис.", "При групова операция проверете кои записи са запазени."])
        } finally {
            setWorking(false);
        }
    }


    function updateEndTime(e: Date | null) {
        if (!e || e < editCalendarEvent?.item?.eventDayStart!) return;
        setEndTime(e);
        const diffInHours = timeDiffInMinutes(editCalendarEvent?.item?.eventDayStart!, e, 0) / 60;
        if (diffInHours > 24) {
            setErrors(["Внимание! Отчитате повече от 24часа. Моля, проверете крайния час!"]);
        } else {
            setErrors([]);
        }
    }

    return (
        <Modal show onHide={() => hideModal()} size={"lg"}>
            <Modal.Header>
                <Modal.Title className={"w-100"}>
                    <Row className={"justify-content-between align-content-between"}>
                        <Col xs={"auto"}>{editCalendarEvent?.employee?.name || "- Не е избран служител -"}</Col>
                        <Col className={"text-primary"}>{editCalendarEvent?.calendarDay.date}</Col>
                        <Col className={"small text-nowrap"}>
                            Отработени часове:&nbsp;
                            {
                                endTime ? <span className={"text-primary"}>{
                                    timeDiffToString(editCalendarEvent?.item?.eventDayStart!, endTime, restTime)
                                }</span> : "-"
                            }
                            &nbsp;
                        </Col>
                    </Row>
                </Modal.Title>
                <CloseButton onClick={() => hideModal()}/>
            </Modal.Header>
            <Form onSubmit={e => handleFormSubmit(e)}>
                <Modal.Body>
                    <WarehouseLockNotice access={lockAccess}/>
                    <Form.Group>
                        <Form.Label>Събитие:</Form.Label>
                        <Form.Control readOnly disabled value={editCalendarEvent?.event?.eventName}/>
                    </Form.Group>
                    <Row>
                        <Col xs={"auto"}>
                            <Form.Group>
                                <Form.Label>Начален час:</Form.Label>
                                <Form.Control readOnly disabled
                                              value={dateTimeToString(editCalendarEvent.item?.eventDayStart)}/>
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group>
                                <Form.Label>Бележки от планирането:</Form.Label>
                                <Form.Control as={"textarea"} readOnly disabled
                                              value={editCalendarEvent?.item?.description}/>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className={"mt-3"}>
                        <Form.Label className={"mb-3 fw-bold"}>Опции:</Form.Label>
                        <Row className={"flex-nowrap bg-secondary-light p-1 lh-1"}>
                            <Col>
                                <Row className={"flex-nowrap justify-content-between"}>
                                    <Col xs={"auto"}>
                                        <Row className={"flex-nowrap"}>
                                            <Col xs={"auto"} className={"lh-2-5em me-0 pe-0"}>Краен час:</Col>
                                            <Col xs={"auto"}>
                                                <ReactDatePicker onChange={date => updateEndTime(date || new Date())}
                                                                 disabled={isReadOnly}
                                                                 required name={"startDate"}
                                                                 dateFormat="dd.MM.yyyy HH:mm"
                                                                 selected={endTime}
                                                                 className={"form-control"}
                                                                 showTimeSelect
                                                                 timeCaption={"Край:"}
                                                                 timeIntervals={30}
                                                                 minDate={editCalendarEvent?.item?.eventDayStart}
                                                                 value={dateTimeToString(endTime)}/>
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col xs={"auto"}>
                                        <Row className={"flex-nowrap"}>
                                            <Col xs={"auto"} className={"lh-2-5em me-0 pe-0"}>
                                                Почивка:&nbsp;&nbsp;&nbsp;
                                            </Col>
                                            <Col xs={"auto"}>
                                                <NumberInputField label={""}
                                                                  disabled={isReadOnly}
                                                                  fieldClass={"form-control-sm w-50px lh-2-5em border border-2 border-dark"}
                                                                  value={restTime}
                                                                  formatter={value => zeroToEmptyString(value)}
                                                                  onChange={newValue => setRestTime(newValue)}
                                                />
                                            </Col>
                                            <Col className={"lh-2-5em me-0 pe-0"}>
                                                мин.
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col xs={"auto"}>
                                        <Row className={"flex-nowrap"}>
                                            <Col xs={"auto"} className={"lh-2-5em me-0 pe-0"}>
                                                Бонус:&nbsp;&nbsp;&nbsp;
                                            </Col>
                                            <Col xs={"auto"}>
                                                <NumberInputField label={""}
                                                                  disabled={isReadOnly}
                                                                  fieldClass={"form-control-sm w-50px lh-2-5em border border-2 border-dark"}
                                                                  value={bonus}
                                                                  formatter={value => zeroToEmptyString(value)}
                                                                  onChange={currentEmployee?.isSuperAdmin ? newValue => setBonus(newValue) : undefined}
                                                />
                                            </Col>
                                            <Col className={"lh-2-5em me-0 pe-0"}>
                                                €.
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col xs={"auto"} className={"text-end"}>
                                        <Row className={"flex-nowrap"}>
                                            <Col xs={"auto"} className={"lh-2-5em me-0 pe-0"}>
                                                Оценка:&nbsp;&nbsp;&nbsp;
                                            </Col>
                                            <Col>
                                                {
                                                    isSupervisor ?
                                                        <ListGroup horizontal
                                                                   activeKey={face === "NOT SET" ? undefined : face}
                                                                   onSelect={eventKey => setFace(eventKey || "GOOD")}
                                                        >
                                                            <ListGroup.Item eventKey={"GOOD"} disabled={isReadOnly}>
                                                                <EventFaceIcon face={"GOOD"}/>
                                                            </ListGroup.Item>
                                                            <ListGroup.Item eventKey={"NEUTRAL"}
                                                                            disabled={isReadOnly}>
                                                                <EventFaceIcon face={"NEUTRAL"}/>
                                                            </ListGroup.Item>
                                                            <ListGroup.Item eventKey={"BAD"} disabled={isReadOnly}>
                                                                <EventFaceIcon face={"BAD"}/>
                                                            </ListGroup.Item>
                                                        </ListGroup> :
                                                        <div className={"lh-2-5em pt-2 "}><EventFaceIcon
                                                            face={editCalendarEvent?.item?.face || ""}/></div>
                                                }
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Form.Group>

                    <Row className={"mt-2"}>
                        <Col>
                            {
                                errors.length > 0 &&
                                <Alert variant={"danger"}>
                                    <ul className={"mb-0"}>
                                        {
                                            errors.map((error, index) => <li key={index}>{error}</li>)
                                        }
                                    </ul>
                                </Alert>
                            }
                        </Col>
                    </Row>
                    <Form.Group>
                        <Form.Label className={"mb-3 fw-bold"}>Бележки:</Form.Label>
                        <FormControl as={"textarea"} rows={3} value={notes || ""} disabled={isReadOnly}
                                     onChange={event => setNotes(event.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    {
                        working ?
                            <Col xs={12}><Spinner/></Col>
                            :
                            <>
                            {
                                !isReadOnly &&
                                <Col xs={"auto"}>
                                    <Button type={"submit"} variant={"primary"}>
                                        <FontAwesomeIcon icon={faCheck}/>&nbsp;
                                        Запази {selectedEmployees.length ? "за всички избрани" : ""}</Button>
                                </Col>
                            }
                                <Col className={"pt-2 ps-2"}>
                                    <Form.Check checked={isConfirmed} id={"isConfirmed"}
                                                label={"Потвърдено от супервайзор"}
                                                onChange={event => setIsConfirmed(event.target.checked)}
                                                disabled={isReadOnly}
                                    />
                                </Col>
                                <Col className={"text-end"}>
                                    <CancelIconButton onClick={() => hideModal()}/>
                                </Col>
                            </>
                    }
                </Modal.Footer>
            </Form>
        </Modal>
    )
}

export default ReportCalendarEvent;
